import React from 'react';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { useRouter } from 'next/router';

import {
  useMediaQueryBreakPoint,
  IS_PHONE_OR_TABLET,
  useOnScreen,
} from '@/src/hooks';
import { ListMode } from '../SearchResults/types';
import TlcCardContent from './TlcCardContent';

interface Props {
  mode?: ListMode;
  item: Required<CardHcd>;
  className?: string;
  waitingVideoSessions: Array<any>;
  withTheSamePractitioner: boolean;
  onClick?: React.ReactEventHandler;
}

const TlcCardAssembled = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      mode,
      item,
      className,
      waitingVideoSessions,
      withTheSamePractitioner,
      ...props
    },
    ref,
  ) => {
    const [isDisplayedOnScreen, setIsDisplayedOnScreened] = React.useState(
      false,
    );
    useOnScreen(ref as React.RefObject<HTMLDivElement>, '300px', val => {
      if (val && !isDisplayedOnScreen) setIsDisplayedOnScreened(true);
    });
    const router = useRouter();
    const fromTlcList = 1;

    const isPhoneOrTablet: boolean = useMediaQueryBreakPoint(
      IS_PHONE_OR_TABLET,
    );

    const goToHcdPage = React.useCallback(
      (e?: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        if (!item.url) {
          return;
        }
        router.push({
          pathname: item.url,
          query: {
            ...(item.center.id ? { centerId: item.center.id } : {}),
            ...(router.pathname.includes('teleconsultation') && {
              fromImmediateTlc: true,
            }),
          },
        });
        e?.stopPropagation();
      },
      [item],
    );

    const onClick = React.useCallback(
      e => {
        if (!e?.tracked)
          (window as any).dataLayer?.push({
            event: 'SearchCardBody_clickOnCard',
            center: item?.center,
            professional: item?.professional,
            type: item?.type,
          });
        if (mode === 'tlc') {
          const tlcParams = {
            'center-id': item?.center?.id ?? '',
            'practitioner-id': item?.professional?.practitionerId ?? '',
            url: item.url,
            fromTlcList,
          };

          if (isPhoneOrTablet) {
            if (props?.onClick) props.onClick(e);
            return;
          }
          if (withTheSamePractitioner) {
            router.push({
              pathname: '/tlc/session/[id]',
              query: {
                id: waitingVideoSessions?.[0].id,
              },
            });
          } else {
            router.push({
              pathname: '/tlc/[center-id]/[practitioner-id]',
              query: tlcParams,
            });
          }
        } else if (item.url) {
          props.onClick?.(e);
          goToHcdPage();
        }
      },
      [
        item,
        mode,
        waitingVideoSessions,
        withTheSamePractitioner,
        isPhoneOrTablet,
      ],
    );

    return (
      <TlcCardContent
        ref={ref} // from SearchCardWrapper, see notes above
        data-testid="search-card" // from SearchCardWrapper, see notes above
        mode={mode}
        item={item}
        waitingVideoSessions={waitingVideoSessions}
        withTheSamePractitioner={withTheSamePractitioner}
        onClick={onClick}
        onAvatarClick={goToHcdPage}
      />
    );
  },
);

TlcCardAssembled.displayName = 'TlcCardAssembled';

export default TlcCardAssembled;
